/**
 * Add/edit provider, update key, add/edit model — the three modals on
 * Providers & Models (mockup board 1). Custom controls only.
 *
 * KEYS ARE WRITE-ONLY. The key modal holds the typed secret in component
 * state only until the PUT resolves, then clears it; nothing is logged,
 * toasted or kept, and the backend never returns it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import type { AiModelDto, AiProviderDto, AiUsageCountsDto } from '../../../services/aiControl/registry';
import {
  putAiModel, putAiProvider, putAiProviderKey, rubyAiErrorText, type ModelPatch, type ProviderPatch,
} from '../../../services/aiControl/rubyAi';
import { USED_FOR_LABELS, USED_FOR_DISPLAY_GROUPS, SELECTABLE_USED_FOR, normalizeUsedFor } from './catalogModel';
import { AI_MODEL_USED_FOR } from './units';
import { Badge, Btn, FieldLabel, Issues, Modal, Mono, TextInput, Toggle } from './ui';

type Toast = (message: string, type?: 'success' | 'error' | 'info') => void;

const ID_RE = /^[a-z0-9][a-z0-9_.-]{0,63}$/i;

// ---------------- provider ----------------

const AUTH_TYPES: Array<{ id: NonNullable<ProviderPatch['authType']>; label: string; hint: string }> = [
  { id: 'header', label: 'API key header', hint: 'Key sent in a request header' },
  { id: 'query', label: 'Query parameter', hint: 'Key sent as ?key=' },
  { id: 'serviceAccount', label: 'Service account', hint: 'Google-style JSON credentials' },
];

export const ProviderEditModal: React.FC<{
  open: boolean; provider: AiProviderDto | null; existingIds: string[];
  onClose: () => void; onSaved: () => Promise<void> | void; showToast: Toast;
}> = ({ open, provider, existingIds, onClose, onSaved, showToast }) => {
  const isNew = !provider;
  const [providerId, setProviderId] = useState('');
  const [label, setLabel] = useState('');
  const [vendor, setVendor] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [authType, setAuthType] = useState<NonNullable<ProviderPatch['authType']>>('header');
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [authPrefix, setAuthPrefix] = useState('');
  const [endpointTemplate, setEndpointTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const p = provider as (AiProviderDto & { authHeaderName?: string; authPrefix?: string }) | null;
    setProviderId(p?.providerId ?? '');
    setLabel(p?.label ?? '');
    setVendor(p?.vendor ?? '');
    setEnabled(p?.enabled ?? true);
    setAuthType(p?.authType ?? 'header');
    setAuthHeaderName(p?.authHeaderName ?? '');
    setAuthPrefix(p?.authPrefix ?? '');
    setEndpointTemplate(p?.endpointTemplate ?? '');
    setServerError(null);
  }, [open, provider]);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (isNew && !ID_RE.test(providerId)) e.push('Provider id: letters, numbers, _ . - (no spaces).');
    if (isNew && existingIds.includes(providerId)) e.push(`A provider "${providerId}" already exists.`);
    if (!label.trim()) e.push('Give the provider a name.');
    if (!vendor.trim()) e.push('Vendor is required (e.g. google, anthropic, openai).');
    return e;
  }, [isNew, providerId, existingIds, label, vendor]);

  const save = async () => {
    setSaving(true);
    setServerError(null);
    const patch: ProviderPatch = {
      label: label.trim(), vendor: vendor.trim(), enabled, authType,
      authHeaderName: authHeaderName.trim() || (isNew ? undefined : null),
      authPrefix: authPrefix || (isNew ? undefined : null),
      endpointTemplate: endpointTemplate.trim() || (isNew ? undefined : null),
    };
    try {
      await putAiProvider(isNew ? providerId : provider!.providerId, patch);
      showToast(`${label.trim()} ${isNew ? 'added' : 'saved'}`, 'success');
      await onSaved();
      onClose();
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to save the provider'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} busy={saving} wide
      title={isNew ? 'Add provider' : `Edit ${provider?.label}`}
      subtitle={isNew ? 'The key is added afterwards with “Update key” — it never travels with the provider row.' : <Mono>{provider?.credentialsRef}</Mono>}
      footer={<>
        <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn kind="primary" onClick={() => void save()} busy={saving} disabled={errors.length > 0}>{isNew ? 'Add provider' : 'Save provider'}</Btn>
      </>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel htmlFor="pv-id">Provider id</FieldLabel>
          <TextInput id="pv-id" value={providerId} disabled={!isNew} onChange={(e) => setProviderId(e.target.value.trim())} placeholder="elevenlabs" />
        </div>
        <div>
          <FieldLabel htmlFor="pv-label">Name</FieldLabel>
          <TextInput id="pv-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ElevenLabs" />
        </div>
        <div>
          <FieldLabel htmlFor="pv-vendor">Vendor</FieldLabel>
          <TextInput id="pv-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="elevenlabs" />
        </div>
        <div>
          <FieldLabel>Enabled</FieldLabel>
          <Toggle on={enabled} onChange={setEnabled} label="Provider enabled" />
        </div>
      </div>
      <div>
        <FieldLabel>How the key is sent</FieldLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {AUTH_TYPES.map((t) => (
            <button key={t.id} type="button" onClick={() => setAuthType(t.id)} aria-pressed={authType === t.id}
              className={`min-h-[44px] rounded-lg border px-3 py-2 text-left ${authType === t.id ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239]' : 'border-stone-300 bg-white text-stone-800'}`}>
              <span className="block text-sm font-semibold">{t.label}</span>
              <span className="block text-[11px] text-stone-600">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>
      {authType !== 'serviceAccount' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel htmlFor="pv-hdr">{authType === 'query' ? 'Query parameter name' : 'Header name'}</FieldLabel>
            <TextInput id="pv-hdr" value={authHeaderName} onChange={(e) => setAuthHeaderName(e.target.value)} placeholder={authType === 'query' ? 'key' : 'x-api-key'} />
          </div>
          <div>
            <FieldLabel htmlFor="pv-prefix">Value prefix (optional)</FieldLabel>
            <TextInput id="pv-prefix" value={authPrefix} onChange={(e) => setAuthPrefix(e.target.value)} placeholder="Bearer " />
          </div>
        </div>
      )}
      <div>
        <FieldLabel htmlFor="pv-ep">Endpoint template (optional)</FieldLabel>
        <TextInput id="pv-ep" value={endpointTemplate} onChange={(e) => setEndpointTemplate(e.target.value)} placeholder="https://api.example.com/v1/{model}" />
      </div>
      <Issues errors={serverError ? [...errors, serverError] : errors} />
    </Modal>
  );
};

export const ProviderKeyModal: React.FC<{
  open: boolean; provider: AiProviderDto | null; onClose: () => void;
  onSaved: (providerId: string, at: string) => void; showToast: Toast;
}> = ({ open, provider, onClose, onSaved, showToast }) => {
  const [key, setKey] = useState('');
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // The secret never outlives the modal.
  useEffect(() => { if (!open) { setKey(''); setReveal(false); setServerError(null); } }, [open]);

  const isJson = provider?.authType === 'serviceAccount';
  const tooShort = key.trim().length > 0 && key.trim().length < 8;

  const save = async () => {
    if (!provider) return;
    setSaving(true);
    setServerError(null);
    const value = key.trim();
    setKey('');
    try {
      const r = await putAiProviderKey(provider.providerId, value);
      showToast(`${provider.label} key updated`, 'success');
      onSaved(provider.providerId, r.updatedAt);
      onClose();
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to update the key'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} busy={saving}
      title={`Update key — ${provider?.label ?? ''}`}
      subtitle={<>Written to the SSM SecureString <Mono>{provider?.credentialsRef}</Mono>. It is never shown again, here or anywhere.</>}
      footer={<>
        <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn kind="primary" onClick={() => void save()} busy={saving} disabled={!key.trim() || tooShort}>
          <KeyRound size={16} /> Save key
        </Btn>
      </>}
    >
      <div>
        <FieldLabel htmlFor="pv-key">{isJson ? 'Service account JSON' : 'API key'}</FieldLabel>
        <div className="relative">
          <input
            id="pv-key"
            type={reveal ? 'text' : 'password'}
            autoComplete="new-password"
            spellCheck={false}
            data-lpignore="true"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={isJson ? 'Paste the JSON on one line' : 'Paste the new key'}
            className="w-full pl-3 pr-14 min-h-[44px] text-sm font-mono border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-500"
          />
          <button type="button" onClick={() => setReveal((r) => !r)} aria-label={reveal ? 'Hide key' : 'Show key'}
            className="absolute right-0 top-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-stone-500">
            {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p className="text-[11px] text-stone-600 mt-1.5">Rotating a key takes effect on the next call. No redeploy.</p>
      </div>
      <Issues errors={[...(tooShort ? ['That is too short to be a real key.'] : []), ...(serverError ? [serverError] : [])]} />
    </Modal>
  );
};

// ---------------- model ----------------

const PRICE_FIELDS: Array<{ key: string; label: string; unit: string }> = [
  { key: 'input_per_million_usd', label: 'Input tokens', unit: '$ per 1M' },
  { key: 'output_per_million_usd', label: 'Output tokens', unit: '$ per 1M' },
  { key: 'cache_write_per_million_usd', label: 'Cache write', unit: '$ per 1M' },
  { key: 'cache_read_per_million_usd', label: 'Cache read', unit: '$ per 1M' },
  { key: 'per_image_usd', label: 'Per image', unit: '$ each' },
  { key: 'per_second_usd', label: 'Per second', unit: '$ per s' },
  { key: 'per_call_usd', label: 'Per call', unit: '$ each' },
];

/** Blank and non-numeric entries are omitted — never written as NaN or 0. */
export function pricesFromForm(form: Record<string, string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const { key } of PRICE_FIELDS) {
    const raw = (form[key] ?? '').trim();
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

const TYPICAL_FIELDS: Array<{ key: keyof AiUsageCountsDto; label: string }> = [
  { key: 'inputTokens', label: 'Typical input tokens' },
  { key: 'outputTokens', label: 'Typical output tokens' },
  { key: 'images', label: 'Typical images' },
  { key: 'calls', label: 'Typical calls' },
  { key: 'seconds', label: 'Typical seconds' },
];

/** Same blank/non-numeric-omitted rule as prices. Used for the exchange-rate card's "typical run" column. */
export function typicalUsageFromForm(form: Record<string, string>): AiUsageCountsDto {
  const out: AiUsageCountsDto = {};
  for (const { key } of TYPICAL_FIELDS) {
    const raw = (form[key] ?? '').trim();
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

const IMAGE_CAPS: string[] = USED_FOR_DISPLAY_GROUPS.find((g) => g.heading === 'Image')?.caps ?? [];

export const ModelEditModal: React.FC<{
  open: boolean; model: AiModelDto | null; providers: AiProviderDto[]; models: AiModelDto[];
  onClose: () => void; onSaved: () => Promise<void> | void; showToast: Toast;
}> = ({ open, model, providers, models, onClose, onSaved, showToast }) => {
  const isNew = !model;
  const [providerId, setProviderId] = useState('');
  const [modelId, setModelId] = useState('');
  const [label, setLabel] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [usedFor, setUsedFor] = useState<string[]>([]);
  const [other, setOther] = useState<string[]>([]);
  const [needsReview, setNeedsReview] = useState<string[]>([]);
  // A stored ambiguous "image" survives an untouched save (fix round 1,
  // 2026-10-04): it is only dropped once the admin EXPLICITLY resolves it —
  // by turning on one of the four image capabilities, or by saying this
  // model is not an image model at all. Reset with `needsReview` on open.
  const [needsReviewResolved, setNeedsReviewResolved] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [version, setVersion] = useState('');
  const [typical, setTypical] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const n = normalizeUsedFor(model?.capabilities, AI_MODEL_USED_FOR);
    setProviderId(model?.providerId ?? '');
    setModelId(model?.modelId ?? '');
    setLabel(model?.label ?? '');
    setEnabled(model?.enabled ?? true);
    setUsedFor(n.usedFor);
    setOther(n.other);
    setNeedsReview(n.needsReview);
    setNeedsReviewResolved(false);
    const p: Record<string, string> = {};
    for (const { key } of PRICE_FIELDS) {
      const v = model?.unitPrices?.[key];
      p[key] = typeof v === 'number' ? String(v) : '';
    }
    setPrices(p);
    setVersion(model?.version ?? '');
    const t: Record<string, string> = {};
    for (const { key } of TYPICAL_FIELDS) {
      const v = model?.typicalUsage?.[key];
      t[key] = typeof v === 'number' ? String(v) : '';
    }
    setTypical(t);
    setServerError(null);
  }, [open, model]);

  const badPrices = PRICE_FIELDS.filter(({ key }) => {
    const raw = (prices[key] ?? '').trim();
    return raw !== '' && !(Number.isFinite(Number(raw)) && Number(raw) >= 0);
  });

  const errors = useMemo(() => {
    const e: string[] = [];
    if (isNew && !providerId) e.push('Choose the provider.');
    if (isNew && !ID_RE.test(modelId)) e.push('Model id: the vendor’s id, e.g. claude-haiku-4-5 (no spaces).');
    if (isNew && models.some((m) => m.providerId === providerId && m.modelId === modelId)) e.push('That model is already registered.');
    if (!label.trim()) e.push('Give the model a name.');
    if (!usedFor.length) e.push('Pick at least one “Used for” — actions only offer models whose “Used for” matches.');
    if (badPrices.length) e.push(`Prices must be 0 or more: ${badPrices.map((b) => b.label).join(', ')}.`);
    return e;
  }, [isNew, providerId, modelId, models, label, usedFor, badPrices]);

  const priced = Object.keys(pricesFromForm(prices)).length > 0;

  const save = async () => {
    setSaving(true);
    setServerError(null);
    // Unrecognised stored capabilities (e.g. "something_made_up") are KEPT —
    // this form only edits the used-for vocabulary it shows. Ambiguous
    // needs-review values (plain "image") are KEPT too until the admin
    // EXPLICITLY resolves them (a four-way chip, or "Not an image model") —
    // an untouched save must not silently drop stored data.
    const typicalUsage = typicalUsageFromForm(typical);
    const capabilities = [...usedFor, ...other, ...(needsReviewResolved ? [] : needsReview)];
    const patch: ModelPatch = {
      label: label.trim(), enabled, capabilities, unitPrices: pricesFromForm(prices),
      version: version.trim() || (isNew ? undefined : null),
      typicalUsage: Object.keys(typicalUsage).length ? typicalUsage : (isNew ? undefined : null),
    };
    try {
      await putAiModel(isNew ? providerId : model!.providerId, isNew ? modelId : model!.modelId, patch);
      showToast(`${label.trim()} ${isNew ? 'added' : 'saved'}`, 'success');
      await onSaved();
      onClose();
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to save the model'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open} onClose={onClose} busy={saving} wide
      title={isNew ? 'Add model' : `Edit ${model?.label}`}
      subtitle={isNew ? 'Price it on the unit the vendor bills us — our price is always cost × multiplier on that unit.' : <Mono>{model?.providerId}/{model?.modelId}</Mono>}
      footer={<>
        <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn kind="primary" onClick={() => void save()} busy={saving} disabled={errors.length > 0}>{isNew ? 'Add model' : 'Save model'}</Btn>
      </>}
    >
      {isNew && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FieldLabel>Provider</FieldLabel>
            <SearchableDropdown
              options={providers.map((p) => ({ value: p.providerId, label: p.label, subtitle: p.vendor }))}
              value={providerId || null} onChange={(v) => setProviderId(v || '')}
              placeholder="Choose a provider" searchPlaceholder="Search providers…" ariaLabel="Provider"
            />
          </div>
          <div>
            <FieldLabel htmlFor="md-id">Model id</FieldLabel>
            <TextInput id="md-id" value={modelId} onChange={(e) => setModelId(e.target.value.trim())} placeholder="gemini-2.5-flash" />
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <FieldLabel htmlFor="md-label">Name</FieldLabel>
          <TextInput id="md-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Gemini Flash" />
        </div>
        <div>
          <FieldLabel>On</FieldLabel>
          <Toggle on={enabled} onChange={setEnabled} label="Model enabled" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <FieldLabel htmlFor="md-version">Model version</FieldLabel>
          <TextInput id="md-version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2025-08 (vendor snapshot id, informational)" />
        </div>
      </div>
      <div className="space-y-3">
        <FieldLabel>Used for</FieldLabel>
        {USED_FOR_DISPLAY_GROUPS.map((g) => (
          <div key={g.heading}>
            <p className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide mb-1.5">{g.heading}</p>
            <div className="flex flex-wrap gap-2">
              {g.caps.map((u) => {
                const on = usedFor.includes(u);
                return (
                  <button key={u} type="button" aria-pressed={on}
                    onClick={() => {
                      // Turning ON any of the four image capabilities is an
                      // explicit resolution of a stored ambiguous "image" —
                      // it is dropped on save from this point on, even if
                      // this exact chip is toggled off again afterwards.
                      if (!on && IMAGE_CAPS.includes(u)) setNeedsReviewResolved(true);
                      setUsedFor((cur) => (on ? cur.filter((x) => x !== u) : SELECTABLE_USED_FOR.filter((x) => x === u || cur.includes(x))));
                    }}
                    className={`min-h-[44px] px-3 rounded-lg border text-sm ${on ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239] font-semibold' : 'border-stone-300 bg-white text-stone-700'}`}>
                    {USED_FOR_LABELS[u] ?? u}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {needsReview.length > 0 && !needsReviewResolved && (
          <div className="flex flex-wrap items-center gap-2">
            {needsReview.map((u) => (
              <Badge key={u} tone="amber">{USED_FOR_LABELS[u] ?? u} — needs review</Badge>
            ))}
            <button type="button" onClick={() => setNeedsReviewResolved(true)}
              className="min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white text-stone-700 text-sm">
              Not an image model
            </button>
            <p className="text-[11px] text-stone-600 w-full">
              Ambiguous — could mean draw, edit, find or read. Pick a specific capability above, or say it is not an image
              model at all; until then it is kept as stored.
            </p>
          </div>
        )}
        {other.length > 0 && (
          <p className="text-[11px] text-stone-600 mt-1.5">Also stored (kept as is): <Mono>{other.join(', ')}</Mono></p>
        )}
      </div>
      <div>
        <FieldLabel>Vendor price (what the vendor charges us)</FieldLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRICE_FIELDS.map((f) => (
            <div key={f.key}>
              <span className="block text-[11px] text-stone-600 mb-1">{f.label}</span>
              <TextInput inputMode="decimal" aria-label={f.label} value={prices[f.key] ?? ''} suffix={f.unit}
                onChange={(e) => setPrices((p) => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
        {!priced && <p className="text-[11px] text-amber-800 mt-1.5">No price set — an action cannot charge for this model until it has one.</p>}
      </div>
      <div>
        <FieldLabel>Typical usage (for the exchange-rate card's "typical run" estimate)</FieldLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TYPICAL_FIELDS.map((f) => (
            <div key={f.key}>
              <span className="block text-[11px] text-stone-600 mb-1">{f.label}</span>
              <TextInput inputMode="decimal" aria-label={f.label} value={typical[f.key] ?? ''}
                onChange={(e) => setTypical((t) => ({ ...t, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>
      <Issues errors={serverError ? [...errors, serverError] : errors} />
    </Modal>
  );
};
